import type { TypedArray } from '@kitware/vtk.js/types';
import type { Orientation } from '@src/store/dicom-web/classification-typing';

import z from 'zod';

export interface EncryptedPayload {
  encryptedKey: string;
  iv: string;
  ciphertext: string;
}

export type PayloadImageData
  = | number[]
    | TypedArray
    | EncryptedPayload
    | ArrayBuffer
    | ArrayLike<number>;

export interface DicomPullRequest {
  StudyInstanceUID: string;
  SeriesInstanceUID: string;
  SOPInstanceUID: string;
  imageData?: PayloadImageData;
  dimensions?: number[];
}

export interface ImageUIDLookupTable {
  [key: string]: {
    studyUID: string;
    seriesUID: string;
    imageUID: string;
    sliceIndex: number;
    imageIndex: number;
    realImageIndex: number; // The actual index of the image in the original series, before any cropping or exclusions
  };
}

export const AnalysisSelectedSeriesSchema = z.object({
  seriesUID: z.string(),
  seriesIdx: z.number(),
  sliceIndex: z.number(),
  analysisSliceIndex: z.number(), // The index of the slice in the order it will be analyzed (viewer order), which may differ from the original slice index due to cropping or exclusions
});
export type AnalysisSelectedSeries = z.infer<typeof AnalysisSelectedSeriesSchema>;

export interface MetaData {
  orientationBySeries: {
    [key: string]: number[];
  };
  classificationBySeries: {
    [key: string]: Orientation;
  };
  imagesAreAttached?: boolean; // Optional property for sending images directly to backend without fetching from orthanc instance
  imagesAreCompressed?: boolean; // Optional property for sending compressed images to backend
  imagesAreEncrypted?: boolean; // Optional property for sending encrypted images to backend
  orthancServerBaseURL?: string; // Optional property for Orthanc server base URL
  orthancJwt?: string | null; // Optional property for Orthanc JWT token
  postProcessing?: 'raw' | 'smooth'; // Optional property for post-processing type
  bmoreTarget?: Record<string, [number, number]>; // Optional property for BMore target points
  breathHoldCrops?: Record<string, { start: number; end: number }>; // Optional property for breathhold crop information
  sliceClassification?: Record<string, Orientation>; // Optional property for orientation by analysis slice index (viewer order)
  calculateBMORE?: boolean; // Optional property to enable/disable BMORE analysis
  calculateBMOREMax?: boolean; // Optional property to enable/disable BMORE Max analysis
  calculateVasoconstrictionMap?: boolean; // Optional property to enable/disable vasoconstriction map analysis
  batchImagesTogether?: boolean; // Optional property to indicate if images are batched together
  excludedImageUIDs?: string[]; // Optional property for excluded image UIDs
  selectedSeries: AnalysisSelectedSeries[];
}

export interface ImageRetrivalInfo {
  viewerIDs: string[];
  include?: string[]; // Optional: If provided, only include these imageUIDs
  exclude?: string[]; // Optional: If provided, exclude these imageUIDs
  requiresImageLookup?: boolean; // Optional: If true, requires image lookup from the store
}
